import Api from "js/core/api";
import { ShowConfirmationDialog } from "js/react/components/Dialogs/BaseDialog";

/**
 * Grants our app offline access to user's Google Drive.
 */
async function grantOfflineAccess(auth2) {
    const { code } = await auth2.grantOfflineAccess({
        scope: [
            "profile",
            "https://www.googleapis.com/auth/drive.file",
            "https://www.googleapis.com/auth/drive.install"
        ].join(" "),
        prompt: "consent"
    });

    await Api.gDriveCode.post({ code });
}

/**
 * Grants our app offline access to user's Google Drive.
 * If the popup is blocked, uses app.dialogManager to call auth2.grantOfflineAccess() synchronously to avoid the popup being blocked.
 */
function grantOfflineAccessWithDialogIfNeeded(auth2) {
    // have to call auth2.grantOfflineAccess() within an onclick callback to prevent the popup from being blocked
    return new Promise((resolve, reject) => {
        grantOfflineAccess(auth2)
            .then(() => resolve())
            .catch(err => {
                if (err.error !== "popup_blocked_by_browser") {
                    reject(err);
                    return;
                }

                ShowConfirmationDialog({
                    title: "Connect Google Drive",
                    message: "Beautiful.ai needs access to your Google Drive, press OK to open Google's consent page",
                    acceptCallback: () => {
                        grantOfflineAccess(auth2)
                            .then(() => resolve())
                            .catch(err => reject(err));
                    },
                    cancelCallback: () => {
                        reject();
                    }
                });
            })
            .catch(err => reject(err));
    });
}

/**
 * Saves a presentation with the specified id into the specified Google Drive folder,
 * if no folder specified, the presentation will be saved in the Google Drive root
 */
async function savePresentation(presentationId, folderId = null) {
    const { fileId } = await Api.gDrivePresentation.post({ presentationId, folderId });
    return fileId;
}

/**
 * Removes a presentation with the specified id from Google Drive
 */
async function removePresentation(presentationId) {
    await Api.gDrivePresentation.delete({ presentationId });
}

/**
 * Returns an url to a presentation related to the specified GDrive file depengind on
 * permissions the calling user has (player or editor)
 */
async function getPresentationUrl(fileId) {
    const { presentationUrl } = await Api.gDrivePresentationUrl.get({ fileId });
    return presentationUrl;
}

/**
 * Creates Beautiful.ai folder and saves all the user's presentations into it
 * The calling user must have GDrive offline access granted to B.ai
 */
async function enable() {
    return Api.gDriveEnable.post();
}

/**
 * Removes Beautiful.ai folder and all saved presentations
 * Also removes GDrive offline access tokens for the calling user
 */
async function disable() {
    await Api.gDriveDisable.post();
}

/**
 * Returns the current state of GDrive integration for the calling user
 */
async function getState() {
    return Api.gDriveState.get();
}

/**
 * Returns the id of an associted with the supplied presentation GDrive file
 */
async function getFileId(presentationId) {
    try {
        const { fileId } = await Api.gDriveFileId.get({ presentationId });
        return fileId;
    } catch (err) {
        if (err.code === "notfound") {
            return null;
        }
        console.error(err);
        throw err;
    }
}

module.exports = { savePresentation, getFileId, getPresentationUrl, removePresentation, enable, disable, getState, grantOfflineAccess, grantOfflineAccessWithDialogIfNeeded };
